import PropTypes from 'prop-types';
import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import ScheduleContainer from './containers/ScheduleContainer';
import WelcomeContainer from './containers/WelcomeContainer';
import BreakfastContainer from './containers/BreakfastContainer';
import DinnerContainer from './containers/DinnerContainer';
import EntertainmentContainer from './containers/EntertainmentContainer';
import EntertainmentDetailContainer from './containers/EntertainmentDetailContainer';
import SwimmingContainer from './containers/SwimmingContainer';
import SwimmingDetailContainer from './containers/SwimmingDetailContainer';
import LoginContainer from './containers/LoginContainer';
import HeaderContainer from './containers/HeaderContainer';
import FooterContainer from './containers/FooterContainer';
import EventContainer from './containers/EventContainer';
import CustomRouteContainer from './containers/CustomRouteContainer';
import SummaryContainer from './containers/SummaryContainer';
import ArrivalTimeContainer from './containers/ArrivalTimeContainer';
import ManageCookies from './components/manageCookies/ManageCookies';
import DineAroundContainer from './containers/DineAroundContainer';
import { schedulePage, swimmingDetailsPage, summaryPage, entertainmentPage, dineAroundPage, swimmingPage } from './utils';
import { virtualPageView } from './googleTagManager';
import './App.scss';
import DineAroundDetailContainer from './containers/DineAroundDetailContainer';

const useQuery = () => new URLSearchParams(useLocation().search);

const setBackground = () => {
  const siteCode = Cookies.get('_siteCode');
  switch (siteCode) {
    case 'BG':
      document.body.classList.add('bognor');
      break;
    case 'MH':
      document.body.classList.add('minehead');
      break;
    case 'SK':
      document.body.classList.add('skegness');
      break;
    default:
      document.body.classList.add('default');
  }
};

const PageContent = () => {
  const query = useQuery();
  const path = query.get('page') || '';
  const eventId = query.get('eventId');
  const activityId = query.get('entertainmentId');
  const secretCode = query.get('secretCode');
  setBackground();

  const isDaBooking = Cookies.get('_isDaBooking') === 'true';
  const numberOfIndicators = isDaBooking ? 4 : 5;

  switch (path) {
    case '': {
      return (
        <CustomRouteContainer>
          <HeaderContainer showLogo />
          <WelcomeContainer />
          <ManageCookies />
        </CustomRouteContainer>
      );
    }
    case 'login': {
      return (
        <CustomRouteContainer>
          <HeaderContainer backgroundType="transparent" showLogo />
          <LoginContainer secretCode={secretCode} />
          <ManageCookies />
        </CustomRouteContainer>
      );
    }
    case 'arrival-time': {
      return (
        <CustomRouteContainer privateRoute>
          <HeaderContainer />
          <ArrivalTimeContainer />
          <FooterContainer
            activeIndicator={1}
            redirectPath={dineAroundPage}
            numberOfIndicators={numberOfIndicators}
            showIndicators
          />
        </CustomRouteContainer>
      );
    }
    case 'dine-around': {
      return (
        <CustomRouteContainer privateRoute isDineAroundPage>
          <HeaderContainer minimalHeader showLogo />
          <DineAroundContainer />
          <FooterContainer
            activeIndicator={2}
            redirectPath={swimmingPage}
            numberOfIndicators={numberOfIndicators}
            showIndicators
          />
        </CustomRouteContainer>
      );
    }
    case 'dine-around-detail': {
      return (
        <CustomRouteContainer privateRoute isDineAroundPage>
          <HeaderContainer icon="arrow" />
          <DineAroundDetailContainer dineAroundId={activityId} />
        </CustomRouteContainer>
      );
    }
    case 'breakfast': {
      return (
        <CustomRouteContainer privateRoute>
          <HeaderContainer minimalHeader showLogo />
          <BreakfastContainer />
          <FooterContainer
            activeIndicator={2}
            redirectPath={schedulePage}
            numberOfIndicators={numberOfIndicators}
            showIndicators
          />
        </CustomRouteContainer>
      );
    }
    case 'dinner': {
      return (
        <CustomRouteContainer privateRoute>
          <HeaderContainer minimalHeader showLogo />
          <DinnerContainer />
          <FooterContainer
            activeIndicator={3}
            redirectPath={schedulePage}
            numberOfIndicators={numberOfIndicators}
            showIndicators
          />
        </CustomRouteContainer>
      );
    }
    case 'swimming': {
      return (
        <CustomRouteContainer privateRoute isSwimmingPage>
          <HeaderContainer minimalHeader showLogo />
          <SwimmingContainer detailPage={swimmingDetailsPage} isSwimmingPage />
          <FooterContainer
            activeIndicator={isDaBooking ? 3 : 4}
            redirectPath={entertainmentPage}
            numberOfIndicators={numberOfIndicators}
            showIndicators
            isSwimmingPage
          />
        </CustomRouteContainer>
      );
    }
    case 'swimming-detail': {
      return (
        <CustomRouteContainer privateRoute isSwimmingPage>
          <HeaderContainer icon="arrow" />
          <SwimmingDetailContainer splashId={activityId} isSwimmingPage />
        </CustomRouteContainer>
      );
    }
    case 'entertainment': {
      return (
        <CustomRouteContainer privateRoute isEntertainmentPage>
          <HeaderContainer minimalHeader showLogo />
          <EntertainmentContainer />
          <FooterContainer
            activeIndicator={isDaBooking ? 4 : 5}
            redirectPath={schedulePage}
            numberOfIndicators={numberOfIndicators}
            showIndicators
            isEntertainmentListPage
          />
        </CustomRouteContainer>
      );
    }
    case 'entertainment-detail': {
      return (
        <CustomRouteContainer privateRoute isEntertainmentPage>
          <HeaderContainer icon="arrow" />
          <EntertainmentDetailContainer entertainmentId={activityId} />
        </CustomRouteContainer>
      );
    }
    case 'schedule': {
      return (
        <CustomRouteContainer privateRoute>
          <HeaderContainer backgroundType="red" showProfile icon="logout" showLogo />
          <ScheduleContainer />
          <ManageCookies />
          <FooterContainer redirectPath={summaryPage} buttonType="book-more" />
        </CustomRouteContainer>
      );
    }
    case 'event': {
      return (
        <CustomRouteContainer privateRoute>
          <HeaderContainer backgroundType="red" icon="arrow" showLogo />
          <EventContainer eventId={eventId} />
          <FooterContainer eventId={eventId} buttonType="edit" />
        </CustomRouteContainer>
      );
    }
    case 'summary': {
      return (
        <CustomRouteContainer privateRoute>
          <HeaderContainer icon="arrow" />
          <div className="summary-page">
            <SummaryContainer />
            {isDaBooking && <DineAroundContainer isSummaryPage />}
            <SwimmingContainer detailPage={swimmingDetailsPage} isSummaryPage />
            <EntertainmentContainer isSummaryPage />
          </div>
          <FooterContainer buttonType="done" redirectPath={schedulePage} />
        </CustomRouteContainer>
      );
    }
    default: {
      virtualPageView('error', 'not found', '/ga-virtual/error/not-found');
      return (
        <CustomRouteContainer notFoundErrorMessage="Page not found" />
      );
    }
  }
};

const App = ({ store }) => (
  <Provider store={store}>
    <div className="App">
      <BrowserRouter>
        <PageContent />
      </BrowserRouter>
    </div>
  </Provider>
);

App.propTypes = {
  store: PropTypes.object.isRequired,
};

export default App;
